import { formatPilotageCount, formatPilotageCurrency } from "@/lib/pilotageUi";

const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeMissionId = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue || normalizedValue.toLowerCase() === "null") {
    return "";
  }

  return normalizedValue;
};

const LEFT_STATUS = new Set([
  "SORTI",
  "LEFT",
  "INACTIF",
  "INACTIVE",
  "INACTIVE_COLLABORATOR",
]);

const ACTIVE_STATUS = new Set([
  "ACTIVE",
  "ACTIF",
  "EN MISSION",
  "EN_MISSION",
]);

const INTERCONTRACT_STATUS = new Set([
  "INTERCONTRAT",
  "INTERCONTRACT",
]);

export const resolveCollaboratorsRevenueStatus = (status, missionId, collaboratorStatus) => {
  const normalizedCollabStatus = String(collaboratorStatus || "").trim().toUpperCase();
  if (LEFT_STATUS.has(normalizedCollabStatus)) {
    return "Sorti";
  }

  const normalizedStatus = String(status || "").trim().toUpperCase();
  if (LEFT_STATUS.has(normalizedStatus)) {
    return "Sorti";
  }

  if (ACTIVE_STATUS.has(normalizedStatus)) {
    return "En mission";
  }

  if (INTERCONTRACT_STATUS.has(normalizedStatus)) {
    return "Intercontrat";
  }

  if (["ENDED", "CLOSED", "TERMINATED"].includes(normalizedStatus)) {
    if (normalizedCollabStatus && LEFT_STATUS.has(normalizedCollabStatus)) {
      return "Sorti";
    }
  }

  return normalizeMissionId(missionId) ? "En mission" : "Intercontrat";
};

export const normalizeCollaboratorsRevenueRows = (rows = []) =>
  Array.isArray(rows)
    ? rows.map((row) => {
      const missionId = normalizeMissionId(row?.mission_id);
      const workedDays = toNumber(row?.worked_days);
      const billableDays = toNumber(row?.billable_days);
      const retainedDays = toNumber(
        row?.retained_days ?? (workedDays > 0 ? workedDays : billableDays),
      );
      const cjm = toNumber(row?.cjm);
      const consultantCost = toNumber(
        row?.consultant_cost ?? row?.collaborator_cost ?? (cjm * retainedDays),
      );
      const expenses = toNumber(row?.expenses);
      const realCost = toNumber(
        row?.real_cost ?? (consultantCost + expenses),
      );
      const caAnnuel = toNumber(row?.ca_annuel_net ?? row?.revenue);
      const realMargin = toNumber(
        row?.real_margin ?? row?.benefice_annuel ?? (caAnnuel - realCost),
      );
      const realCjm = toOptionalNumber(
        row?.real_cjm ?? (retainedDays > 0 ? realCost / retainedDays : null),
      );
      const realMarginPct = toOptionalNumber(
        row?.real_margin_pct ?? (caAnnuel > 0 ? (realMargin / caAnnuel) * 100 : null),
      );
      const tjmClient = toNumber(row?.tjm_client);
      const tjmNet = toNumber(
        row?.tjm_net ?? (retainedDays > 0 ? caAnnuel / retainedDays : tjmClient),
      );
      const marginBeforeExpenses = toNumber(
        row?.margin_before_expenses ?? (caAnnuel - consultantCost),
      );

      return {
        missionId,
        clientName: row?.client_name ? String(row.client_name) : null,
        missionLabel: row?.mission_label ? String(row.mission_label) : null,
        missionRole: row?.mission_role ? String(row.mission_role) : null,
        consultantLabel: String(row?.collaborator_name || "Collaborateur inconnu"),
        consultantEmail: String(row?.collaborator_email || ""),
        collaborator_id: String(row?.collaborator_id || ""),
        status: resolveCollaboratorsRevenueStatus(row?.status, missionId, row?.collaborator_status),
        caMensuelEstime: toNumber(row?.ca_mensuel_net),
        coutMensuel: toOptionalNumber(row?.cout_mensuel),
        beneficeMensuel: toOptionalNumber(row?.benefice_mensuel),
        margeMensuellePct: toOptionalNumber(row?.marge_mensuelle_pct),
        selectedMonth: toOptionalNumber(row?.selected_month),
        caAnnuelEstime: caAnnuel,
        revenue: caAnnuel,
        beneficeAnnuelEstime: realMargin,
        workedDays,
        billableDays,
        startDate: row?.start_date ? String(row.start_date) : null,
        endDate: row?.end_date ? String(row.end_date) : null,
        etpContribution: toOptionalNumber(
          row?.etp_contribution ?? (billableDays > 0 ? billableDays / 210 : 0)
        ),
        retainedDays,
        tjmClient,
        tjmNet,
        cjm,
        consultantCost,
        collaboratorCost: consultantCost,
        expenses,
        realCost,
        realCjm,
        marginBeforeExpenses,
        realMargin,
        realMarginPct,
        contractType: String(row?.contract_type || ""),
        freeDays: toNumber(row?.free_days),
        theoreticalMarginPct: toOptionalNumber(row?.theoretical_margin_pct),
        marginGapPctPoints: toOptionalNumber(row?.margin_gap_pct_points),
        freeDaysCost: toNumber(row?.free_days_cost),
        lostRevenue: toNumber(row?.lost_revenue),
        marginWithIntermediaryPct: toOptionalNumber(
          row?.margin_with_intermediary_pct ?? row?.marge_pct_net,
        ),
        marginWithoutIntermediaryPct: toOptionalNumber(
          row?.margin_without_intermediary_pct ?? row?.marge_pct,
        ),
        margePct: toOptionalNumber(
          row?.margin_with_intermediary_pct ?? row?.marge_pct_net ?? row?.marge_pct,
        ),
      };
    })
    : [];

export const getRealCjmTooltipText = (row) => {
  const baseDescription =
    "CJM réel = coût journalier du collaborateur augmenté des frais collaborateur imputables à la période, rapporté au nombre de jours retenus.";

  const retainedDays = row?.retainedDays ?? row?.workedDays ?? (row?.billableDays || 0);
  const cjm = Number.isFinite(row?.cjm) ? row.cjm : 0;
  const expenses = Number.isFinite(row?.expenses) ? row.expenses : 0;

  if (!Number.isFinite(retainedDays) || retainedDays <= 0) {
    return `${baseDescription}\n\nJours retenus : 0. CJM réel non calculable (division par zéro évitée).\nFrais imputables : ${formatPilotageCurrency(expenses)}`;
  }

  const realCjm = Number.isFinite(row?.realCjm)
    ? row.realCjm
    : Math.round((cjm + expenses / retainedDays) * 100) / 100;

  return `${baseDescription}\n\nDétail du calcul :\nCJM : ${formatPilotageCurrency(cjm)}/j\nFrais : ${formatPilotageCurrency(expenses)}\nJours : ${formatPilotageCount(retainedDays)}\n\n${formatPilotageCount(cjm)} + (${formatPilotageCount(expenses)} / ${formatPilotageCount(retainedDays)}) = ${formatPilotageCurrency(realCjm)}/j`;
};
